/**
 * Minimal client for the OpenAI Realtime API over WebRTC.
 *
 * Flow (per OpenAI Realtime docs):
 *   1. Backend mints an ephemeral client_secret token (~60 s) with the
 *      session pre-configured (persona, tools, voice, instructions).
 *   2. We create a peer connection, add the microphone track and a data
 *      channel named "oai-events".
 *   3. We create an SDP offer, POST it to
 *      https://api.openai.com/v1/realtime/calls?model=<model> with the
 *      ephemeral token as bearer, and set the returned SDP as remote answer.
 *   4. Audio flows over the peer connection (server VAD is enabled), events
 *      (function calls, transcripts, response state) come over the data
 *      channel.
 *
 * The class is UI-agnostic: it emits events via callbacks so React can drive
 * the visual state without knowing about WebRTC.
 */

const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

export class LexiaRealtimeSession {
    constructor({ onEvent, onState, onError, onTranscriptUser, onTranscriptAssistant, onToolCall }) {
        this.onEvent = onEvent || (() => {});
        this.onState = onState || (() => {});
        this.onError = onError || (() => {});
        this.onTranscriptUser = onTranscriptUser || (() => {});
        this.onTranscriptAssistant = onTranscriptAssistant || (() => {});
        this.onToolCall = onToolCall || (async () => ({}));
        this.pc = null;
        this.dc = null;
        this.stream = null;
        this.audioEl = null;
        this.closed = false;
        this._assistantBuf = "";
    }

    async connect({ ephemeralToken, model }) {
        this.onState("connecting");
        // --- audio sink -----------------------------------------------------
        this.audioEl = document.createElement("audio");
        this.audioEl.autoplay = true;
        this.audioEl.playsInline = true;

        // --- peer connection -----------------------------------------------
        this.pc = new RTCPeerConnection();
        this.pc.ontrack = (e) => {
            if (this.audioEl && e.streams && e.streams[0]) {
                this.audioEl.srcObject = e.streams[0];
            }
        };
        this.pc.oniceconnectionstatechange = () => {
            if (!this.pc) return;
            const s = this.pc.iceConnectionState;
            if (s === "failed" || s === "disconnected") {
                this.onError(new Error("Connexion WebRTC interrompue."));
                this.close();
            }
        };

        // --- microphone -----------------------------------------------------
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (err) {
            this.onState("mic_denied");
            throw new Error("Accès micro refusé : autorisez le micro dans votre navigateur pour parler avec Lexia.");
        }
        this.stream.getTracks().forEach((t) => this.pc.addTrack(t, this.stream));

        // --- data channel for events ---------------------------------------
        this.dc = this.pc.createDataChannel("oai-events");
        this.dc.addEventListener("message", (e) => this._handleEvent(e.data));

        // --- SDP handshake with OpenAI -------------------------------------
        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);

        const r = await fetch(`${REALTIME_CALLS_URL}?model=${encodeURIComponent(model)}`, {
            method: "POST",
            body: offer.sdp,
            headers: {
                Authorization: `Bearer ${ephemeralToken}`,
                "Content-Type": "application/sdp",
            },
        });
        if (!r.ok) {
            const txt = await r.text();
            throw new Error(`OpenAI Realtime a refusé l'appel (${r.status}) : ${txt.slice(0, 160)}`);
        }
        const answerSdp = await r.text();
        await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

        this.onState("listening");
    }

    /** Wait for the data channel to open, with a timeout. */
    async waitForDataChannel(timeoutMs = 4000) {
        if (!this.dc) throw new Error("Data channel absent.");
        if (this.dc.readyState === "open") return;
        await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error("Data channel timeout")), timeoutMs);
            this.dc.addEventListener("open", () => { clearTimeout(timer); resolve(); }, { once: true });
        });
    }

    /**
     * Send a user text message (used for the initial greeting or for tests).
     * The assistant will reply vocally.
     */
    async sendText(text) {
        if (!this.dc || this.dc.readyState !== "open") return;
        this.dc.send(JSON.stringify({
            type: "conversation.item.create",
            item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
        }));
        this.dc.send(JSON.stringify({ type: "response.create" }));
    }

    setMicMuted(muted) {
        if (!this.stream) return;
        this.stream.getAudioTracks().forEach((t) => { t.enabled = !muted; });
    }

    _handleEvent(raw) {
        let evt;
        try { evt = JSON.parse(raw); } catch { return; }
        this.onEvent(evt);
        // Transcript deltas
        if (evt.type === "response.output_audio_transcript.delta" && evt.delta) {
            this._assistantBuf += evt.delta;
            this.onTranscriptAssistant(this._assistantBuf, /* final */ false);
        }
        if (evt.type === "response.output_audio_transcript.done" && evt.transcript !== undefined) {
            this.onTranscriptAssistant(evt.transcript, true);
            this._assistantBuf = "";
        }
        if (evt.type === "conversation.item.input_audio_transcription.completed" && evt.transcript) {
            this.onTranscriptUser(evt.transcript);
        }
        // Assistant speaking / listening states
        if (evt.type === "response.created") this.onState("speaking");
        if (evt.type === "response.done") this.onState("listening");
        // Function calls
        if (evt.type === "response.function_call_arguments.done") {
            this._runTool(evt);
        }
    }

    async _runTool(evt) {
        let args = {};
        try { args = evt.arguments ? JSON.parse(evt.arguments) : {}; } catch { args = {}; }
        let output;
        try {
            output = await this.onToolCall(evt.name, args);
        } catch (err) {
            output = { error: err?.message || "Erreur d'outil." };
        }
        if (!this.dc || this.dc.readyState !== "open") return;
        this.dc.send(JSON.stringify({
            type: "conversation.item.create",
            item: {
                type: "function_call_output",
                call_id: evt.call_id,
                output: typeof output === "string" ? output : JSON.stringify(output || {}),
            },
        }));
        this.dc.send(JSON.stringify({ type: "response.create" }));
    }

    close() {
        if (this.closed) return;
        this.closed = true;
        try { this.dc && this.dc.close(); } catch { /* noop */ }
        try {
            this.stream && this.stream.getTracks().forEach((t) => t.stop());
        } catch { /* noop */ }
        try { this.pc && this.pc.close(); } catch { /* noop */ }
        try {
            if (this.audioEl) {
                this.audioEl.srcObject = null;
                this.audioEl.pause();
            }
        } catch { /* noop */ }
        this.pc = null;
        this.dc = null;
        this.stream = null;
        this.audioEl = null;
        this.onState("closed");
    }
}
